import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import {
  Code,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';

// Types for results display
interface Answer {
  uuid: string;
  questionId: string;
  questionContent: string;
  content: string;
  recorder: string;
  frames: string;
  duration: number;
  language: string;
  metadata?: {
    timestamp: string;
    [key: string]: any;
  };
}

interface AnswersDisplayProps {
  answers: Answer[];
  selectedQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
}

const AnswersDisplay: React.FC<AnswersDisplayProps> = ({
  answers,
  selectedQuestionIndex,
  onQuestionSelect,
}) => {
  if (!answers || answers.length === 0) {
    return (
      <Alert severity="info">
        No answers found for this interview.
      </Alert>
    );
  }

  const currentAnswer = answers[selectedQuestionIndex] || answers[0];


  return (
    <Grid container spacing={3}>
      {/* Question List */}
      <Grid item xs={12} md={4}>
        <Paper elevation={1} sx={{ height: 'fit-content' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              Questions ({answers.length})
            </Typography>
          </Box>
          
          <List sx={{ p: 0 }}>
            {answers.map((answer, index) => (
              <React.Fragment key={answer.uuid}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={index === selectedQuestionIndex}
                    onClick={() => onQuestionSelect(index)}
                    sx={{ py: 2 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {answer.content ? (
                        <CheckCircle color="success" sx={{ mr: 2, fontSize: 20 }} />
                      ) : (
                        <RadioButtonUnchecked color="disabled" sx={{ mr: 2, fontSize: 20 }} />
                      )}
                      
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          Question {index + 1}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {answer.questionContent ? 
                            answer.questionContent.substring(0, 50) + (answer.questionContent.length > 50 ? '...' : '')
                            : `Question ${index + 1} content`
                          }
                        </Typography>
                        {answer.content && (
                          <Typography variant="caption" color="success.main" noWrap>
                            {answer.language || 'Text'} solution submitted
                          </Typography>
                        )}
                      </Box>
                      
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < answers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Grid>

      {/* Selected Answer Content */}
      <Grid item xs={12} md={8}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Question and Answer Content */}
          <Paper elevation={1} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Question Section */}
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Question {selectedQuestionIndex + 1}
                </Typography>
                <Chip 
                  label={currentAnswer.language || 'Text'} 
                  size="small" 
                  color="primary" 
                />
              </Box>
              
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                {currentAnswer.questionContent || `Question ${selectedQuestionIndex + 1} content`}
              </Typography>
            </Box>

            {/* Answer Section */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Code fontSize="small" />
                <Typography variant="subtitle2">
                  Candidate's Solution
                </Typography>
                {currentAnswer.content && (
                  <Chip 
                    icon={<CheckCircle />}
                    label="Submitted" 
                    color="success" 
                    size="small" 
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ p: 3, flexGrow: 1 }}>
              {currentAnswer.content ? (
                <pre
                  style={{
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    margin: 0,
                    padding: '16px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    overflow: 'auto',
                  }}
                >
                  {currentAnswer.content}
                </pre>
              ) : (
                <Alert severity="warning">
                  No answer submitted for this question.
                </Alert>
              )}
            </Box>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AnswersDisplay;